import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';

/**
 * Error boundary component for the Experience Builder
 *
 * Catches JavaScript errors anywhere in the child component tree and displays
 * a fallback UI with recovery options instead of crashing the entire application.
 *
 * Features:
 * - Displays detailed error information for debugging
 * - Provides "Try Again" option to reload the current experience
 * - Provides "Delete Experience & Start Fresh" option to delete corrupted data
 * - Shows development information (activation ID, timestamp, user agent)
 * - Logs errors to console for debugging
 *
 * Usage:
 * Wrap any component that might throw errors, typically at the top level
 * of the Experience Builder to catch all errors within the experience editing flow.
 */

interface Props {
    children: ReactNode;
    activationId: number;
    onDeleteExperience?: () => void;
    onRestart?: () => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ExperienceErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): State {
        // Update state so the next render will show the fallback UI
        return {
            hasError: true,
            error,
            errorInfo: null,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log the error for debugging
        console.error('Experience Builder Error:', error);
        console.error('Error Info:', errorInfo);

        this.setState({
            error,
            errorInfo,
        });
    }

    handleRestart = () => {
        // Reset the error boundary state
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        // Call the restart callback if provided
        if (this.props.onRestart) {
            this.props.onRestart();
        }
    };

    handleDeleteExperience = () => {
        if (this.props.onDeleteExperience) {
            this.props.onDeleteExperience();
        }

        // Reset the error boundary state after deletion
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                    <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
                        {/* Header */}
                        <div className="flex items-center mb-6">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-8 w-8 text-red-500" />
                            </div>
                            <div className="ml-4">
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Experience Builder Error
                                </h1>
                                <p className="text-gray-600 mt-1">
                                    Something went wrong while loading the
                                    experience.
                                </p>
                            </div>
                        </div>

                        {/* Error Details */}
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">
                                Error Details
                            </h2>
                            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                                <p className="text-red-800 font-medium mb-2">
                                    {this.state.error?.name}:{' '}
                                    {this.state.error?.message}
                                </p>
                                {this.state.errorInfo && (
                                    <details className="mt-3">
                                        <summary className="text-red-700 cursor-pointer hover:text-red-900">
                                            Show Stack Trace
                                        </summary>
                                        <pre className="mt-2 text-xs text-red-600 whitespace-pre-wrap overflow-auto max-h-40 bg-red-100 p-2 rounded border">
                                            {this.state.error?.stack}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        </div>

                        {/* Recovery Options */}
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">
                                Recovery Options
                            </h2>
                            <p className="text-gray-600 mb-4">
                                Choose one of the following options to recover
                                from this error:
                            </p>

                            <div className="space-y-3">
                                {/* Restart Option */}
                                <button
                                    onClick={this.handleRestart}
                                    className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                >
                                    <RefreshCw className="h-5 w-5 mr-2" />
                                    Try Again
                                    <span className="ml-2 text-sm text-gray-500">
                                        (Reload the current experience)
                                    </span>
                                </button>

                                {/* Delete Experience Option */}
                                {this.props.onDeleteExperience && (
                                    <button
                                        onClick={this.handleDeleteExperience}
                                        className="w-full flex items-center justify-center px-4 py-3 border border-red-300 rounded-md shadow-sm bg-red-50 text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                    >
                                        <Trash2 className="h-5 w-5 mr-2" />
                                        Delete Experience & Start Fresh
                                        <span className="ml-2 text-sm text-red-500">
                                            (Deletes the current experience and
                                            reloads the page)
                                        </span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Development Info */}
                        <div className="border-t border-gray-200 pt-6">
                            <h3 className="text-sm font-medium text-gray-900 mb-2">
                                Development Information
                            </h3>
                            <div className="text-sm text-gray-600 space-y-1">
                                <p>
                                    <strong>Activation ID:</strong>{' '}
                                    {this.props.activationId}
                                </p>
                                <p>
                                    <strong>Timestamp:</strong>{' '}
                                    {new Date().toISOString()}
                                </p>
                                <p>
                                    <strong>User Agent:</strong>{' '}
                                    {navigator.userAgent}
                                </p>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="mt-6 text-center">
                            <p className="text-xs text-gray-500">
                                If this error persists, please contact the
                                development team with the error details above.
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ExperienceErrorBoundary;
